import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { rupiah, tgl } from "../../lib/format";
import { PdfButton, dl } from "./shared";

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 font-bold text-[10px] uppercase tracking-wide ${right ? "text-right" : ""}`}>{children}</th>
);

function TersediaTable({ items }) {
  return (
    <table className="w-full text-sm min-w-[680px]" data-testid="stok-table-tersedia">
      <thead>
        <tr className="border-b border-border bg-muted/60 text-left">
          <Th>Kode</Th><Th>Nama Barang</Th><Th>Kategori</Th><Th>Kondisi</Th><Th>Tgl Masuk</Th><Th right>Harga Beli</Th><Th right>Jual Rencana</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {items.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-muted-foreground">Tidak ada barang tersedia.</td></tr>}
        {items.map((i) => (
          <tr key={i.id} data-testid={`stok-report-${i.id}`}>
            <td className="px-3 py-2.5 font-semibold">{i.kode}</td>
            <td className="px-3 py-2.5">{i.nama}</td>
            <td className="px-3 py-2.5">{i.kategori}</td>
            <td className="px-3 py-2.5">{i.kondisi}</td>
            <td className="px-3 py-2.5">{tgl(i.tanggal_beli)}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(i.harga_beli)}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(i.harga_jual_estimasi)}</td>
          </tr>
        ))}
        {items.length > 0 && (
          <tr className="bg-muted/60 font-bold" data-testid="stok-total-tersedia">
            <td className="px-3 py-2.5" colSpan={5}>Total ({items.length} unit)</td>
            <td className="px-3 py-2.5 text-right">{rupiah(items.reduce((s, i) => s + i.harga_beli, 0))}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(items.reduce((s, i) => s + (i.harga_jual_estimasi || 0), 0))}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function TerjualTable({ items }) {
  return (
    <table className="w-full text-sm min-w-[680px]" data-testid="stok-table-terjual">
      <thead>
        <tr className="border-b border-border bg-muted/60 text-left">
          <Th>Kode</Th><Th>Nama Barang</Th><Th>Kategori</Th><Th>Tgl Jual</Th><Th right>Harga Beli</Th><Th right>Harga Jual</Th><Th right>Laba</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {items.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-muted-foreground">Belum ada barang terjual.</td></tr>}
        {items.map((i) => (
          <tr key={i.id} data-testid={`stok-report-terjual-${i.id}`}>
            <td className="px-3 py-2.5 font-semibold">{i.kode}</td>
            <td className="px-3 py-2.5">{i.nama}</td>
            <td className="px-3 py-2.5">{i.kategori}</td>
            <td className="px-3 py-2.5">{tgl(i.tanggal_jual)}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(i.harga_beli)}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(i.harga_jual)}</td>
            <td className={`px-3 py-2.5 text-right font-semibold ${(i.harga_jual || 0) - i.harga_beli >= 0 ? "text-emerald-600" : "text-red-600"}`}>{rupiah((i.harga_jual || 0) - i.harga_beli)}</td>
          </tr>
        ))}
        {items.length > 0 && (
          <tr className="bg-muted/60 font-bold" data-testid="stok-total-terjual">
            <td className="px-3 py-2.5" colSpan={4}>Total ({items.length} unit)</td>
            <td className="px-3 py-2.5 text-right">{rupiah(items.reduce((s, i) => s + i.harga_beli, 0))}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(items.reduce((s, i) => s + (i.harga_jual || 0), 0))}</td>
            <td className="px-3 py-2.5 text-right">{rupiah(items.reduce((s, i) => s + (i.harga_jual || 0) - i.harga_beli, 0))}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

export default function StokPanel() {
  const [stok, setStok] = useState(null);
  const [stokTab, setStokTab] = useState("tersedia");

  useEffect(() => {
    api.get("/reports/stok").then((r) => setStok(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <div className="flex items-center justify-between mt-4 mb-2">
        <h2 className="text-base font-bold tracking-tight">Laporan Stok Barang</h2>
        <PdfButton testid="stok-pdf-button" onClick={() => dl("/reports/stok/pdf", "laporan-stok.pdf")} />
      </div>
      {stok && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3" data-testid="stok-summary">
            <button onClick={() => setStokTab("tersedia")} data-testid="stok-summary-tersedia"
              className={`rounded-md border p-3 text-left transition-colors duration-150 active:scale-[0.98] ${stokTab === "tersedia" ? "border-primary bg-primary/5" : "border-border bg-white"}`}>
              <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">Tersedia</p>
              <p className="text-lg font-bold">{stok.unit_tersedia} <span className="text-xs font-normal text-muted-foreground">unit</span></p>
            </button>
            <button onClick={() => setStokTab("terjual")} data-testid="stok-summary-terjual"
              className={`rounded-md border p-3 text-left transition-colors duration-150 active:scale-[0.98] ${stokTab === "terjual" ? "border-primary bg-primary/5" : "border-border bg-white"}`}>
              <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">Terjual</p>
              <p className="text-lg font-bold">{stok.unit_terjual} <span className="text-xs font-normal text-muted-foreground">unit</span></p>
            </button>
            <div className="rounded-md border border-border bg-white p-3"><p className="text-[10px] uppercase font-bold text-muted-foreground tracking-wide">Nilai Stok</p><p className="text-sm font-bold">{rupiah(stok.nilai_stok)}</p></div>
          </div>
          <div className="rounded-md border border-border bg-white overflow-x-auto" data-testid="stok-report">
            {stokTab === "tersedia" ? <TersediaTable items={stok.items} /> : <TerjualTable items={stok.items_terjual || []} />}
          </div>
        </>
      )}
    </>
  );
}
